import type { Role } from '../pages/users/types/rbac';

/**
 * Roles that may open `/dashboard/settings/users/:id` for **another** user's full profile.
 * Everyone may open their **own** id (session profile FHIR id).
 */
const ROLES_CAN_VIEW_ANY_USER_PROFILE = new Set<Role>([
  'unify-admin',
  'practice-owner',
  'practice-admin',
]);

export const USER_ROLE_STORAGE_KEY = 'unify.userRole';

/** Provider-side canonical editor: clients use Client Profile; staff use User Settings. */
export function providerUserProfilePath(
  userId: string,
  role: Role,
  sessionProfileId?: string,
): string {
  if (role === 'client') {
    return `/dashboard/clients/${userId}/profile`;
  }
  if (sessionProfileId && userId === sessionProfileId) {
    return '/dashboard/settings/profile';
  }
  return `/dashboard/settings/users/${userId}`;
}

/** True when the role may open another user's profile (e.g. after creating a user whose id is not the session id). */
export function canViewAnyUserProfile(role: Role): boolean {
  return ROLES_CAN_VIEW_ANY_USER_PROFILE.has(role);
}

export type UserProfileRouteAccess = 'allowed' | 'denied' | 'pending';

/**
 * Route guard: allowed = render page; denied = redirect; pending = wait for profile (then re-check).
 */
export function getUserProfileRouteAccess(
  targetUserId: string | undefined,
  sessionProfileId: string | undefined,
  role: Role,
): UserProfileRouteAccess {
  if (!targetUserId) {
    return 'denied';
  }
  if (ROLES_CAN_VIEW_ANY_USER_PROFILE.has(role)) {
    return 'allowed';
  }
  if (sessionProfileId === undefined) {
    return 'pending';
  }
  return sessionProfileId === targetUserId ? 'allowed' : 'denied';
}

/** For click handlers when `useMedplumProfile()` is already available. Allows navigation while profile is still loading (`pending`); destination page shows a loader then allows or denies. */
export function canNavigateToUserProfilePage(userId: string, sessionProfileId: string | undefined, role: Role): boolean {
  const access = getUserProfileRouteAccess(userId, sessionProfileId, role);
  return access === 'allowed' || access === 'pending';
}
